import React, { useMemo } from 'react';
import { useMutation } from 'hooks/apollo';
import { Button, Col, Form, Row, Checkbox, Alert, Collapse, Select, Input, Space } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'translations/messages';
import theme from 'shared/constants/theme.json';
import Page from 'components/Page';
import { EDIT_ALLOW_PERSONAL_DATA, EDIT_POLICIES } from 'shared/constants/permissions';
import FormLabel from 'components/FormLabel';
import useCustomer from 'hooks/useCustomer';
import Language from 'components/Language';
import RichTextInput from 'components/RichTextInput';
import { gql } from '@apollo/client';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import useUser from 'hooks/useUser';
import useGlobalContext from 'hooks/useGlobalContext';
import messages from './messages';

const { Panel } = Collapse;

const MUTATION = gql`
  mutation CustomerPrivacy(
    $id: ID!,
    $input: UpdateCustomerInput!
  ) {
    updateCustomer(
      id: $id,
      input: $input,
    ) {
      id
      personalDataController
      allowPersonalData
      disableAI
      privacyPolicies {
        id
        type
        language
        url
        content
      }
    }
  }
`;

const LanguagePrivacyPolicy = ({ language, canEdit }) => {
  const name = ['privacyPolicies', language.id];
  const type = Form.useWatch([...name, 'type']);

  return (
    <Row gutter={16}>
      <Col span={6}>
        <Form.Item name={[...name, 'id']} noStyle>
          <input type="hidden" />
        </Form.Item>

        <Form.Item name={[...name, 'type']} label={<FormLabel label={messages.privacyPolicyTypeLabel} description={messages.privacyPolicyTypeDescription} />}>
          <Select disabled={!canEdit} options={[{ value: 'URL', label: 'Link to an external page' }, { value: 'INLINE', label: 'Text in the survey' }]} />
        </Form.Item>
      </Col>
      <Col span={18}>
        {type === 'URL' && (
          <Form.Item name={[...name, 'url']} label="Privacy policy page">
            <Input disabled={!canEdit} />
          </Form.Item>
        )}
      </Col>
      {type === 'INLINE' && (
        <Col span={24}>
          <Form.Item name={[...name, 'content']} label="Privacy policy">
            <RichTextInput readOnly={!canEdit} />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

const CustommerPrivacy = ({ customer }) => {
  const [form] = Form.useForm();
  const { message } = useGlobalContext();
  const [mutate, { loading }] = useMutation(MUTATION, { onCompleted: () => form.resetFields() });
  const { formatMessage: $t } = useIntl();
  const { isSuperUser } = useUser();
  const { hasCustomerPermission } = useCustomer();

  const canEdit = hasCustomerPermission(EDIT_POLICIES);
  const canEditPDC = isSuperUser;
  const canAllowPersonalData = hasCustomerPermission(EDIT_ALLOW_PERSONAL_DATA);

  const onFinish = async (values) => {
    try {
      const input = {
        personalDataController: canEditPDC ? values.personalDataController : undefined,
        allowPersonalData: canAllowPersonalData ? values.allowPersonalData : undefined,
        disableAI: canEdit ? values.disableAI : undefined,
        privacyPolicies: values.privacyPolicies && Object.entries(values.privacyPolicies).map(([language, policy]) => ({
          ...policy,
          language,
        })).filter(x => x.type),
      };

      await mutate({ variables: { id: customer.id, input } });
      message.success($t(messages.saveSuccess));
    } catch (e) {
      message.error($t(messages.saveError));
    }
  };

  const initialValues = useMemo(() => ({
    ...customer,
    privacyPolicies: customer.privacyPolicies.reduce((acc, policy) => ({ ...acc, [policy.language]: policy }), {}),
  }));

  const getPolicyPanelExtra = language => {
    const policy = customer.privacyPolicies.find(p => p.language === language);

    if (!policy) {
      return (
        <Space>
          <WarningOutlined style={{ color: theme['error-color'] }} />
          No policy
        </Space>
      );
    }

    if (policy.type === 'URL') {
      return (
        <Space>
          <CheckOutlined style={{ color: theme['success-color'] }} />
          Link to an external page
        </Space>
      );
    }

    if (policy.type === 'INLINE') {
      return (
        <Space>
          <CheckOutlined style={{ color: theme['success-color'] }} />
          Text in the survey
        </Space>
      );
    }

    return null;
  };

  return (
    <Form layout="vertical" onFinish={onFinish} form={form} initialValues={initialValues}>
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            name="personalDataController"
            valuePropName="checked"
          >
            <Checkbox disabled={!canEditPDC}>
              <FormLabel label={messages.personalDataControllerLabel} description={messages.personalDataControllerDescription} />
            </Checkbox>
          </Form.Item>

          {canAllowPersonalData && (
          <Form.Item
            name="allowPersonalData"
            valuePropName="checked"
          >
            <Checkbox>
              <FormLabel label={messages.allowPersonalDataLabel} description={messages.allowPersonalDataDescription} />
            </Checkbox>
          </Form.Item>
          )}
          <Form.Item name="disableAI" valuePropName="checked">
            <Checkbox disabled={!canEdit}>
              <FormLabel label={messages.disableAILabel} description={messages.disableAIDescription} />
            </Checkbox>
          </Form.Item>
          <Form.Item label={<FormLabel label={messages.privacyPolicyLabel} description={messages.privacyPolicyDescription} />}>
            {customer.surveyLanguages?.length > 0 ? (
              <Collapse accordion>
                {customer.surveyLanguages.map(language => (
                  <Panel
                    header={<Language locale={language.id} languageName={language.name} />}
                    key={language.id}
                    extra={getPolicyPanelExtra(language.id)}
                  >
                    <LanguagePrivacyPolicy language={language} canEdit={canEdit} />
                  </Panel>
                ))}
              </Collapse>
            ) : (
              <div>You need to create a survey before you can add a privacy policy.</div>
            )}
          </Form.Item>
        </Col>
        <Col offset={1} span={7}>
          <Alert
            message={$t(messages.personalDataControllerLabel)}
            description={(
              <FormattedMessage {...messages.personalDataControllerInformation} />
            )}
            type="info"
            showIcon
          />
        </Col>
      </Row>
      {canEdit && (
      <Page.Footer>
        <Button type="primary" htmlType="submit" loading={loading}>
          <FormattedMessage {...globalMessages.save} />
        </Button>
      </Page.Footer>
      )}
    </Form>
  );
};

export default CustommerPrivacy;
